import { useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import SeoHead from "@/components/SeoHead";
import { RATGEBER_PAGES, RATGEBER_ORDER } from "@/data/sattlereiRatgeber";
import { SITE_URL } from "@/seo/data";
import { CaretDown, CheckCircle, Phone, ArrowRight, Warning, MapPin, Armchair } from "@phosphor-icons/react";

const GREEN_BG = "bg-[#55C96B] hover:bg-[#249143]";

const CtaBox = ({ page }) => (
  <div className="rounded-2xl bg-[#EAF8ED] border border-[#cdeed6] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
    <div>
      <p className="font-bold text-slate-900">{page.ctaTitle}</p>
      <p className="text-sm text-slate-600 mt-1">{page.ctaText}</p>
      {page.priceLine && <p className="text-sm font-semibold text-[#249143] mt-1.5">{page.priceLine}</p>}
      {page.priceInfo && <p className="text-xs text-slate-500 mt-2 max-w-xl leading-relaxed">{page.priceInfo}</p>}
    </div>
    {page.bookable ? (
      <Link
        to={page.ctaService ? `/sattlerei?service=${page.ctaService}` : "/sattlerei"}
        className={`inline-flex items-center justify-center h-12 px-6 rounded-xl ${GREEN_BG} text-white font-semibold transition-colors flex-shrink-0`}
        data-testid={`ratgeber-cta-${page.slug}`}
      >
        Preis online berechnen <ArrowRight size={17} className="ml-2" />
      </Link>
    ) : (
      <a
        href="tel:+49621527429"
        className={`inline-flex items-center justify-center h-12 px-6 rounded-xl ${GREEN_BG} text-white font-semibold transition-colors flex-shrink-0`}
        data-testid={`ratgeber-cta-${page.slug}`}
      >
        <Phone size={17} className="mr-2" weight="fill" /> +49 621 527429
      </a>
    )}
  </div>
);

export default function SattlereiRatgeberPage() {
  const { ratgeberSlug } = useParams();
  const page = RATGEBER_PAGES[ratgeberSlug];
  const [openFaq, setOpenFaq] = useState(-1);

  const schema = useMemo(() => {
    if (!page) return [];
    const canonical = `${SITE_URL}/sattlerei/${page.slug}`;
    const items = [
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: "Sattlerei", item: SITE_URL + "/sattlerei" },
          { "@type": "ListItem", position: 3, name: page.nav, item: canonical },
        ],
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@context": "https://schema.org", "@type": "Article",
        headline: page.nav,
        description: page.title,
        mainEntityOfPage: canonical,
        author: { "@type": "Organization", name: "Servus Autoglas — AutoGlas Erol GmbH" },
        publisher: { "@type": "Organization", name: "AutoGlas Erol GmbH" },
        datePublished: "2026-06-15",
        dateModified: "2026-06-20",
      },
    ];
    if (page.serviceSchema) {
      items.push({
        "@context": "https://schema.org", "@type": "Service",
        name: page.serviceSchema.name,
        serviceType: "Autosattlerei",
        url: canonical,
        provider: { "@type": "AutoRepair", name: "Servus Autoglas — AutoGlas Erol GmbH", address: { "@type": "PostalAddress", streetAddress: "Frankenthaler Str. 98", postalCode: "67059", addressLocality: "Ludwigshafen am Rhein", addressCountry: "DE" }, telephone: "+49 621 527429" },
        areaServed: [{ "@type": "City", name: "Ludwigshafen" }, { "@type": "City", name: "Mannheim" }, { "@type": "City", name: "Heidelberg" }, { "@type": "City", name: "Speyer" }, { "@type": "City", name: "Frankenthal" }, { "@type": "City", name: "Worms" }],
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          priceSpecification: { "@type": "PriceSpecification", minPrice: page.serviceSchema.minPrice, ...(page.serviceSchema.maxPrice ? { maxPrice: page.serviceSchema.maxPrice } : {}), priceCurrency: "EUR" },
        },
      });
    }
    return items;
  }, [page]);

  if (!page) return <Navigate to="/sattlerei" replace />;
  const canonical = `${SITE_URL}/sattlerei/${page.slug}`;
  const others = RATGEBER_ORDER.filter((s) => s !== page.slug);

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead title={page.title} description={page.description} canonical={canonical} />
      <Helmet>
        {schema.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>
      <Header />

      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <nav className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#249143]">Start</Link><span>/</span>
            <Link to="/sattlerei" className="hover:text-[#249143]">Sattlerei</Link><span>/</span>
            <span className="text-slate-700 font-medium">{page.nav}</span>
          </nav>
          <span className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EAF8ED] border border-[#cdeed6] text-[#249143] text-xs font-bold uppercase tracking-widest">
            <Armchair size={14} weight="fill" /> {page.badge}
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight" data-testid={`ratgeber-h1-${page.slug}`}>
            {page.h1}
          </h1>
          {page.intro.map((p, i) => (
            <p key={i} className={`${i === 0 ? "mt-5" : "mt-3"} text-slate-600 text-lg leading-relaxed max-w-3xl`}>{p}</p>
          ))}
        </div>
      </section>

      {/* INHALT */}
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBox page={page} />

          <div className="mt-10 space-y-10">
            {page.sections.map((s) => (
              <div key={s.h}>
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">{s.h}</h2>
                {s.ps.map((p, i) => (
                  <p key={i} className="mt-3 text-slate-600 leading-relaxed">{p}</p>
                ))}
                {s.list && (
                  <>
                    {s.listTitle && <p className="mt-3 text-sm font-semibold text-slate-700">{s.listTitle}</p>}
                    <ul className="mt-2 space-y-2">
                      {s.list.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-slate-600 text-sm leading-relaxed">
                          <CheckCircle size={17} weight="fill" className="text-[#55C96B] mt-0.5 flex-shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {s.after && <p className="mt-3 text-slate-600 leading-relaxed">{s.after}</p>}
                {s.warn && (
                  <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
                    <Warning size={20} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700">{s.warn}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* PFLEGE-TIPPS */}
          {page.care && (
          <div className="mt-12 rounded-3xl bg-white border-2 border-[#a9e3b6] p-6 md:p-8" data-testid={`ratgeber-care-${page.slug}`}>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">{page.care.title}</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {page.care.items.map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                  <CheckCircle size={17} weight="fill" className="text-[#55C96B] mt-0.5 flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
          )}

          {/* INFO-HINWEIS bei nicht buchbaren Themen */}
          {page.infoNote && (
            <div className="mt-8 rounded-2xl bg-slate-100 border border-slate-200 p-5" data-testid={`ratgeber-infonote-${page.slug}`}>
              <p className="text-sm text-slate-600 leading-relaxed">{page.infoNote}</p>
              <Link to="/sattlerei" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#249143] hover:underline">
                Zu den buchbaren Sattlerei-Leistungen <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {/* FAQ — Inhalte immer im DOM, Accordion steuert nur Sichtbarkeit */}
          <h2 className="mt-12 text-xl md:text-2xl font-bold text-slate-900 tracking-tight">Häufige Fragen</h2>
          <div className="mt-5 divide-y divide-slate-200 border border-slate-200 rounded-2xl overflow-hidden bg-white" data-testid={`ratgeber-faq-${page.slug}`}>
            {page.faqs.map((f, i) => (
              <div key={f.q}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-semibold text-slate-900 hover:bg-slate-50"
                >
                  {f.q}
                  <CaretDown size={16} className={`flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <div className={`grid transition-[grid-template-rows] duration-300 ${openFaq === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <CtaBox page={page} />
          </div>

          {/* INTERNE LINKS */}
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Weitere Sattlerei-Ratgeber</h2>
              <ul className="mt-3 space-y-2">
                <li><Link to="/sattlerei" className="text-sm text-[#249143] font-semibold hover:underline">Sattlerei: Preis berechnen &amp; Termin buchen</Link></li>
                {others.map((s) => (
                  <li key={s}>
                    <Link to={`/sattlerei/${s}`} className="text-sm text-[#249143] hover:underline">{RATGEBER_PAGES[s].nav}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Sattlerei in Ihrer Stadt</h2>
              <ul className="mt-3 space-y-2">
                <li><Link to="/sattlerei-mannheim" className="text-sm text-[#249143] hover:underline">Autosattlerei Mannheim</Link></li>
                <li><Link to="/sattlerei-heidelberg" className="text-sm text-[#249143] hover:underline">Autosattlerei Heidelberg</Link></li>
                <li><Link to="/sattlerei-speyer" className="text-sm text-[#249143] hover:underline">Autosattlerei Speyer</Link></li>
              </ul>
              <div className="mt-5 flex items-start gap-2 text-slate-600">
                <MapPin size={18} weight="fill" className="text-[#249143] mt-0.5 flex-shrink-0" />
                <span className="text-sm">Servus Autoglas · AutoGlas Erol GmbH<br />Frankenthaler Str. 98, 67059 Ludwigshafen am Rhein</span>
              </div>
            </div>
          </div>

          <p className="mt-10 text-xs text-slate-400">
            Häufig gesucht: {page.keywords.join(" · ")}
          </p>
        </div>
      </section>
    </div>
  );
}
